import React from 'react';
import { Modal, List, Button } from 'antd';
import SearchInput from 'components/Molecules/Search/SearchInput';
import debounce from 'lodash/debounce';
import classes from './Modal.module.scss';
import cn from 'classnames';

const Listing = React.forwardRef((props, ref) => {
  const {
    mode,
    header,
    onSearch,
    renderItem,
    className,
    inputSearch = {},
    valueKey = 'value',
    dataSource = [],
    ...restProps
  } = props;
  const [keyword, setKeyword] = React.useState('');
  const [selected, setSelected] = React.useState([]);

  const isSelectingAll = dataSource.length > 0 && selected.length === dataSource.length;

  const selectedItemKey = React.useMemo(() => {
    return selected.map((item) => item[valueKey]);
  }, [selected, valueKey]);

  const onSelectAll = () => {
    if (isSelectingAll) {
      setSelected([]);
      return;
    }
    setSelected(dataSource.map((item) => item));
  };

  const debounceSearch = React.useCallback(
    debounce((nextKeyword) => {
      if (onSearch) onSearch(nextKeyword);
    }, 300),
    []
  );

  const handleSearch = (keywords) => {
    setKeyword(keywords);
    debounceSearch(keywords);
  };

  const renderHeader = () => {
    if (header) {
      if (typeof header === 'function') {
        return header({ onSelectAll, isSelectingAll });
      }
      if (React.isValidElement(header)) {
        return React.cloneElement(header, { onSelectAll, isSelectingAll });
      }
      return header;
    }
    if (typeof header === 'undefined' && mode === 'select') {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SearchInput
            placeholder="Search..."
            value={keyword}
            onChange={handleSearch}
            {...inputSearch}
          />
          <Button type="link" onClick={onSelectAll}>
            {isSelectingAll ? 'Deselect All' : 'Select All'}
          </Button>
        </div>
      );
    }
    return null;
  };

  const mergedRenderItem = (record, idx) => {
    const isSelected = selectedItemKey.includes(record[valueKey]);
    const onSelect = () => {
      if (isSelected) {
        setSelected((prev) => prev.filter((item) => item[valueKey] !== record[valueKey]));
      } else {
        setSelected((prev) => [...prev, record]);
      }
    };
    return renderItem(record, idx, {
      selected: isSelected,
      onSelect,
      className: classes['list__item'],
    });
  };

  const dataSourceFiltered = React.useMemo(() => {
    if (!keyword) return dataSource;
    if (onSearch) return dataSource;
    return dataSource.filter((item) => {
      const value = item[valueKey]?.toString().toLowerCase() || '';
      return value.includes(keyword.toLowerCase());
    });
  }, [keyword, dataSource, valueKey, onSearch]);

  React.useImperativeHandle(ref, () => ({
    selected,
  }));

  return (
    <List
      {...restProps}
      className={cn(classes.list, className)}
      dataSource={dataSourceFiltered}
      header={renderHeader()}
      renderItem={mergedRenderItem}
    />
  );
});

export const ListingModal = React.forwardRef((props, ref) => {
  const listRef = React.useRef(null);
  const [open, setOpen] = React.useState(true);
  const { afterClose: hookAfterClose, config, ...restProps } = props;
  const [innerConfig, setInnerConfig] = React.useState(config || {});
  const { listConfig, ...modalConfig } = innerConfig || {};

  const afterClose = () => {
    hookAfterClose();
    innerConfig.afterClose?.();
  };

  const close = (...args) => {
    setOpen(false);
    const triggerCancel = args.some((param) => param?.triggerCancel);
    if (triggerCancel) {
      innerConfig.onCancel?.(() => {}, ...args.slice(1));
    }
  };

  React.useImperativeHandle(ref, () => ({
    destroy: () => {
      setInnerConfig((prev) => ({ ...prev, dataSource: [] }));
    },
    update: (newConfig) => {
      setInnerConfig((originConfig) => {
        const nextConfig = typeof newConfig === 'function' ? newConfig(originConfig) : newConfig;

        return {
          ...originConfig,
          ...nextConfig,
        };
      });
    },
  }));

  return (
    <Modal
      {...modalConfig}
      wrapClassName={classes.overlay}
      open={open}
      afterClose={afterClose}
      onCancel={close}
      onOk={() => {
        if (innerConfig.onOk) {
          innerConfig.onOk(listRef.current?.selected || []);
        }
        close();
      }}
      {...restProps}>
      <Listing ref={listRef} {...listConfig} />
    </Modal>
  );
});
